import { Tooltip } from "antd";
import React, { useState } from "react";
import { parseTags } from "views/Findings/utils";

import "./RiskTagsDisplay.less";
import { ParsedTag } from "./riskTagsConstants";

interface RiskTagsDisplayProps {
  tags?: string[];
  showMoreThreshold?: number;
  className?: string;
}

/**
 * Displays risk tags in a vertical list with icons in colored circles
 * Used in the expanded Risk section of Finding Details
 */
export default function RiskTagsDisplay({
  tags,
  showMoreThreshold = 4,
  className = "",
}: RiskTagsDisplayProps) {
  const [showAll, setShowAll] = useState(false);

  if (!tags || tags.length === 0) {
    return null;
  }

  const parsedTags = parseTags(tags);
  const hasMoreTags = parsedTags.length > showMoreThreshold;
  const displayTags = showAll
    ? parsedTags
    : parsedTags.slice(0, showMoreThreshold);

  return (
    <div className={`risk-tags-display ${className}`}>
      <div className="risk-tags-display__list">
        {displayTags.map((tag, index) => (
          <RiskTagItem key={`${tag.key}-${index}`} tag={tag} />
        ))}
      </div>

      {hasMoreTags && (
        <button
          className="risk-tags-display__toggle"
          onClick={() => setShowAll(!showAll)}
          type="button"
        >
          {showAll
            ? "Show less"
            : `+${parsedTags.length - showMoreThreshold} more`}
        </button>
      )}
    </div>
  );
}

interface RiskTagItemProps {
  tag: ParsedTag;
}

function RiskTagItem({ tag }: RiskTagItemProps) {
  const IconComponent = tag.config?.icon;
  const hasIcon = tag.isKnown && IconComponent;
  const keyClass = hasIcon
    ? "risk-tags-display__key"
    : "risk-tags-display__key risk-tags-display__key--no-icon";

  return (
    <div className="risk-tags-display__item">
      {hasIcon && (
        <div
          className="risk-tags-display__icon-wrapper"
          style={{ backgroundColor: tag.color.background }}
        >
          <IconComponent
            className="risk-tags-display__icon"
            style={{ color: tag.color.foreground }}
          />
        </div>
      )}
      <span className={keyClass} title={tag.displayKey}>
        {tag.displayKey}
      </span>
      <span className="risk-tags-display__value" title={tag.displayValue}>
        {tag.displayValue}
      </span>
    </div>
  );
}

interface RiskTagsMiniDisplayProps {
  tags?: string[];
  maxVisible?: number;
  className?: string;
}

/**
 * Displays mini icons in colored circles for the collapsed Risk header
 * - Only shows icons for known tags (with icons)
 * - Unknown tags (without icons) are counted in "+N"
 */
export function RiskTagsMiniDisplay({
  tags,
  maxVisible = 4,
  className = "",
}: RiskTagsMiniDisplayProps) {
  if (!tags || tags.length === 0) {
    return null;
  }

  const parsedTags = parseTags(tags);

  // Separate tags with icons from tags without icons
  const tagsWithIcons = parsedTags.filter(
    (tag) => tag.isKnown && tag.config?.icon,
  );
  const tagsWithoutIconsCount = parsedTags.length - tagsWithIcons.length;

  // Show up to maxVisible icons
  const visibleIconTags = tagsWithIcons.slice(0, maxVisible);

  // Remaining count = icons beyond maxVisible + all tags without icons
  const remainingIconTags = Math.max(0, tagsWithIcons.length - maxVisible);
  const remainingCount = remainingIconTags + tagsWithoutIconsCount;

  return (
    <div className={`risk-tags-mini ${className}`}>
      {/* Render known tags with icons */}
      {visibleIconTags.map((tag, index) => {
        const IconComponent = tag.config?.icon;
        return (
          <Tooltip
            key={`icon-${tag.key}-${index}`}
            title={`${tag.displayKey}: ${tag.displayValue}`}
          >
            <div
              className="risk-tags-mini__icon-wrapper"
              style={{ backgroundColor: tag.color.background }}
            >
              {IconComponent && (
                <IconComponent
                  className="risk-tags-mini__icon"
                  style={{ color: tag.color.foreground }}
                />
              )}
            </div>
          </Tooltip>
        );
      })}

      {remainingCount > 0 && (
        <span className="risk-tags-mini__more">+{remainingCount}</span>
      )}
    </div>
  );
}
